import { HttpClient } from '@angular/common/http';
import { effect, Injectable, signal, untracked } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { environment } from '@environments/environment';
import { ActorType } from '@models/api-calls/application/application.model';
import { AddressDpvDataResponse, ClientResponse } from '@models/api-calls/clients/client-response.model';
import { InsuredClient } from '@models/api-calls/clients/client.model';
import { MainSteps, PersonalDetailsSubSteps } from '@models/config/app-structure.model';
import { AddressValidationService } from '@services/api-calls/address-validation.service';
import { ApplicationService } from '@services/api-calls/application.service';
import { ErrorHandlingService } from '@services/error-handling.service';
import { ApplicationFormService } from '@services/form/application-form.service';
import { FormRegistryService } from '@services/form/form-registry.service';
import { StepperService } from '@services/layout/stepper.service';
import { DateUtil } from '@utils/date.util';
import { AddressType } from 'app/enums/form/address.enum';
import {
    AddressFormEnum,
    ContactInformationFormEnum,
    COUNTRY_CODE_CANADA,
    COUNTRY_CODE_UNITED_STATES,
    IdentityVerificationConsentFormEnum,
    MailingAddressInformationFormEnum,
    PersonalInformationFormEnum,
    ResidencyAndIdentificationFormEnum,
    ResidentialInformationFormEnum,
    YesNoOptionsEnum,
} from 'app/enums/form/form.enum';
import { RESTRICTED_STATES } from 'app/validators/restricted-state.validator';
import { catchError, tap } from 'rxjs';

const API_URL = environment.apiUrl;

@Injectable({
    providedIn: 'root',
})
export class InsuredClientService {
    patchFromServerVersion = signal(0);
    private suppressNextPatch = signal(false);

    constructor(
        private http: HttpClient,
        private applicationService: ApplicationService,
        private applicationFormService: ApplicationFormService,
        private formRegistry: FormRegistryService,
        private stepperService: StepperService,
        private errorHandlingService: ErrorHandlingService,
        private addressValidationService: AddressValidationService,
    ) {
        effect(() => {
            // IMPORTANT: only applicationInsuredClient() should be a tracked dependency.
            // Everything else runs untracked to prevent form value changes / validation state
            // changes from re-triggering this effect and overwriting user input.
            const insuredClient = this.applicationService.applicationInsuredClient();
            if (!insuredClient) return;
            if (untracked(() => this.suppressNextPatch())) {
                this.suppressNextPatch.set(false);
                return;
            }
            untracked(() => this.patchInsuredClientDataOnForm(insuredClient));
        });

        effect(() => {
            // Re-patch mailing address when substep is added
            const newSubsteps = this.stepperService.newlyAddedSubsteps();
            const mailingAddressKey = `${MainSteps.PERSONAL_DETAILS}:${PersonalDetailsSubSteps.MAILING_ADDRESS}`;
            if (newSubsteps.has(mailingAddressKey)) {
                untracked(() => this.patchMailingAddressData());
            }
        });
    }

    createOrUpdateInsuredClient() {
        const insuredClientId = this.applicationService.application()?.insured_client?.id;
        const applicationId = this.applicationService.application()?.id;
        const insuredClientData = this.createModifiedInsuredClientData();
        if (!applicationId || !insuredClientData) {
            return;
        }
        if (!Object.keys(insuredClientData).length) {
            // No changes to update
            return;
        }
        if (insuredClientId) {
            return this.updateInsuredClient(applicationId, insuredClientId, insuredClientData).pipe(
                tap((response: ClientResponse) => {
                    if (response && response.id) {
                        this.applicationService.insuredClientFromResponse.set(response);
                    } else {
                        this.applicationService.refreshApplicationData.update((n) => n + 1);
                    }
                }),
                catchError((error) => this.errorHandlingService.handleSaveError(error)),
            );
        } else {
            return this.createInsuredClient(applicationId, insuredClientData).pipe(
                tap((response: ClientResponse) => {
                    if (response && response.id) {
                        this.applicationService.insuredClientFromResponse.set(response);
                    } else {
                        this.applicationService.refreshApplicationData.update((n) => n + 1);
                    }
                }),
                catchError((error) => this.errorHandlingService.handleSaveError(error)),
            );
        }
    }

    saveResidentialAddress() {
        const insuredClientId = this.applicationService.application()?.insured_client?.id;
        const applicationId = this.applicationService.application()?.id;
        if (!applicationId || !insuredClientId) {
            return;
        }

        // Clear local validation state for residential address
        this.addressValidationService.clearValidationState(AddressType.INSURED_CLIENT_RESIDENTIAL);

        const addressData: Partial<ClientResponse> = {
            address: this.formRegistry.getSafeControlValue<string>([
                MainSteps.PERSONAL_DETAILS,
                PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ADDRESS}`,
            ]),
            city: this.formRegistry.getSafeControlValue<string>([
                MainSteps.PERSONAL_DETAILS,
                PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.CITY}`,
            ]),
            state_code: this.formRegistry.getSafeControlValue<string>([
                MainSteps.PERSONAL_DETAILS,
                PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.STATE_CODE}`,
            ]),
            zip_code: this.formRegistry.getSafeControlValue<string>([
                MainSteps.PERSONAL_DETAILS,
                PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ZIP_CODE}`,
            ]),
            address2: this.formRegistry.getSafeControlValue<string>([
                MainSteps.PERSONAL_DETAILS,
                PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ADDRESS2}`,
            ]),
            country_code: COUNTRY_CODE_UNITED_STATES,
            address_dpv_code: null,
            address_dpv_data: null,
        };

        return this.updateInsuredClient(applicationId, insuredClientId, addressData).pipe(
            tap((response: ClientResponse) => {
                if (response?.id) {
                    this.applicationService.insuredClientFromResponse.set(response);
                } else {
                    this.applicationService.refreshApplicationData.update((n) => n + 1);
                }
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    hasChangesComparedToServer(): boolean {
        const modified = this.createModifiedInsuredClientData();
        const EXCLUDED_FIELDS: (keyof ClientResponse)[] = [
            'address_dpv_code',
            'address_dpv_data',
            'mailing_dpv_code',
            'mailing_dpv_data',
            'smoker',
            'smoker_ext',
            'weight',
            'height_ft',
            'height_in',
            'occupation',
            'annual_income_earned',
            'employer_name',
            'contact_time_am',
            'contact_time_pm',
            'role',
            'country_code',
        ];
        const userChangedFields = Object.keys(modified).filter((k) => !EXCLUDED_FIELDS.includes(k as keyof ClientResponse));
        return userChangedFields.length > 0;
    }

    patchInsuredClientDataOnForm(insuredClient: InsuredClient) {
        const applicationForm = this.applicationFormService.applicationForm;

        // Patch Personal Information
        const personalInformationForm = applicationForm.get(MainSteps.PERSONAL_DETAILS)?.get(PersonalDetailsSubSteps.PERSONAL_INFORMATION);

        const dobDate = DateUtil.fromServer(insuredClient.dob);

        personalInformationForm?.get(PersonalInformationFormEnum.FIRST_NAME)?.patchValue(insuredClient.first_name);
        personalInformationForm?.get(PersonalInformationFormEnum.MIDDLE_NAME)?.patchValue(insuredClient.middle_name);
        personalInformationForm?.get(PersonalInformationFormEnum.LAST_NAME)?.patchValue(insuredClient.last_name);
        personalInformationForm?.get(PersonalInformationFormEnum.DATE_OF_BIRTH)?.patchValue(dobDate as any);
        personalInformationForm?.get(PersonalInformationFormEnum.SEX_AT_BIRTH)?.patchValue(insuredClient.gender);
        // IAPP2-1741: Fields removed
        // personalInformationForm?.get(PersonalInformationFormEnum.SMOKER)?.patchValue(insuredClient.smoker);
        // personalInformationForm?.get(PersonalInformationFormEnum.SMOKER_EXT)?.patchValue(insuredClient.smoker_ext);
        // personalInformationForm?.get(PersonalInformationFormEnum.WEIGHT)?.patchValue(insuredClient.weight);
        // personalInformationForm?.get(PersonalInformationFormEnum.HEIGHT_FT)?.patchValue(insuredClient.height_ft);
        // personalInformationForm?.get(PersonalInformationFormEnum.HEIGHT_IN)?.patchValue(insuredClient.height_in);

        // Patch Finance Information
        // IAPP2-1741: All finance fields removed
        // const financeInformationForm = applicationForm.get(MainSteps.PERSONAL_DETAILS)?.get(PersonalDetailsSubSteps.FINANCE_INFORMATION);
        // financeInformationForm?.get(FinanceInformationFormEnum.OCCUPATION)?.patchValue(insuredClient.occupation);
        // financeInformationForm?.get(FinanceInformationFormEnum.ANNUAL_INCOME_EARNED)?.patchValue(insuredClient.annual_income_earned);
        // financeInformationForm?.get(FinanceInformationFormEnum.EMPLOYER_NAME)?.patchValue(insuredClient.employer_name);

        // Patch Residential Information

        const residentialInformationForm = applicationForm
            .get(MainSteps.PERSONAL_DETAILS)
            ?.get(PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION);

        const residentialInformationFormAddressGroup = residentialInformationForm?.get(ResidentialInformationFormEnum.ADDRESS_GROUP);

        residentialInformationFormAddressGroup?.get(AddressFormEnum.ADDRESS)?.patchValue(insuredClient.address.address);
        residentialInformationFormAddressGroup?.get(AddressFormEnum.CITY)?.patchValue(insuredClient.address.city);
        const stateCodeControl = residentialInformationFormAddressGroup?.get(AddressFormEnum.STATE_CODE);
        stateCodeControl?.patchValue(insuredClient.address.state_code);
        if (insuredClient.address.state_code && RESTRICTED_STATES.includes(insuredClient.address.state_code)) {
            stateCodeControl?.markAsTouched();
            stateCodeControl?.updateValueAndValidity();
        }
        residentialInformationFormAddressGroup?.get(AddressFormEnum.ZIP_CODE)?.patchValue(insuredClient.address.zip_code);
        residentialInformationFormAddressGroup?.get(AddressFormEnum.ADDRESS2)?.patchValue(insuredClient.address.address2);
        this.buildFullAddressFromFields(residentialInformationFormAddressGroup);
        // The Y/N question that fed this control was removed from the UI — the form now
        // defaults to YES so the flag persists once the user reaches a save point. Don't
        // overwrite that default with a null from the backend (which would otherwise
        // round-trip back as null on every save).
        if (insuredClient.has_mailing_address != null) {
            residentialInformationForm?.get(ResidentialInformationFormEnum.HAS_MAILING_ADDRESS)?.patchValue(insuredClient.has_mailing_address);
        }

        // Set validation state from existing DPV data
        this.addressValidationService.setValidationStateFromExisting(
            AddressType.INSURED_CLIENT_RESIDENTIAL,
            insuredClient.address.address_dpv_code,
            insuredClient.address.address_dpv_data,
        );

        // Patch Mailing Address Information (if form exists)
        this.patchMailingAddressData(insuredClient);

        // Patch Contact Information

        const contactInformationForm = applicationForm.get(MainSteps.PERSONAL_DETAILS)?.get(PersonalDetailsSubSteps.CONTACT_INFORMATION);

        contactInformationForm?.get(ContactInformationFormEnum.EMAIL)?.patchValue(insuredClient.email);
        contactInformationForm?.get(ContactInformationFormEnum.PRIMARY_PHONE)?.patchValue(insuredClient.primary_phone);
        // Primary phone type no longer collected from user — preserve form default (MOBILE)
        // when backend has no value yet.
        if (insuredClient.primary_phone_type != null) {
            contactInformationForm?.get(ContactInformationFormEnum.PRIMARY_PHONE_TYPE)?.patchValue(insuredClient.primary_phone_type);
        }
        contactInformationForm?.get(ContactInformationFormEnum.PHONE2)?.patchValue(insuredClient.phone2);
        contactInformationForm?.get(ContactInformationFormEnum.PHONE2_TYPE)?.patchValue(insuredClient.phone2_type);
        // IAPP2-1741: Fields removed
        // contactInformationForm?.get(ContactInformationFormEnum.CONTACT_TIME_AM)?.patchValue(insuredClient.contact_time_am);
        // contactInformationForm?.get(ContactInformationFormEnum.CONTACT_TIME_PM)?.patchValue(insuredClient.contact_time_pm);

        // Patch Residency and Identification

        const residencyAndIdentificationForm = applicationForm
            .get(MainSteps.PERSONAL_DETAILS)
            ?.get(PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION);

        if (insuredClient.birth_country_code) {
            residencyAndIdentificationForm
                ?.get(ResidencyAndIdentificationFormEnum.BIRTH_COUNTRY_CODE)
                ?.patchValue(insuredClient.birth_country_code);
        }
        residencyAndIdentificationForm
            ?.get(ResidencyAndIdentificationFormEnum.BIRTH_STATE_CODE)
            ?.patchValue(insuredClient.birth_state_code);
        residencyAndIdentificationForm?.get(ResidencyAndIdentificationFormEnum.US_CITIZEN)?.patchValue(insuredClient.us_citizen);

        // Disable birth_state_code when country is not US/CA (mirrors component behavior)
        const birthCountryCode = residencyAndIdentificationForm?.get(ResidencyAndIdentificationFormEnum.BIRTH_COUNTRY_CODE)?.value;
        const birthStateControl = residencyAndIdentificationForm?.get(ResidencyAndIdentificationFormEnum.BIRTH_STATE_CODE);
        if (birthCountryCode && birthCountryCode !== COUNTRY_CODE_UNITED_STATES && birthCountryCode !== COUNTRY_CODE_CANADA) {
            if (birthStateControl?.enabled) {
                birthStateControl.disable();
                birthStateControl.clearValidators();
                birthStateControl.updateValueAndValidity();
            }
        }

        // Patch Identity Verification Consent
        const identityVerificationConsentForm = applicationForm
            .get(MainSteps.PERSONAL_DETAILS)
            ?.get(PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT);

        identityVerificationConsentForm?.get(IdentityVerificationConsentFormEnum.SSN)?.patchValue(insuredClient.ssn);
        identityVerificationConsentForm?.get(IdentityVerificationConsentFormEnum.GIACT_STATUS)?.patchValue(insuredClient.giact_status);
        identityVerificationConsentForm?.get(IdentityVerificationConsentFormEnum.GIACT_OUTCOME)?.patchValue(insuredClient.giact_outcome);

        const giactDisclosureControl = identityVerificationConsentForm?.get(IdentityVerificationConsentFormEnum.GIACT_DISCLOSURE);
        giactDisclosureControl?.patchValue(insuredClient.giact_disclosure);
        if (insuredClient.giact_disclosure === YesNoOptionsEnum.YES) {
            giactDisclosureControl?.disable();
        }

        untracked(() => this.patchFromServerVersion.update((n) => n + 1));
    }

    private patchMailingAddressData(insuredClient?: InsuredClient | null): void {
        insuredClient ??= this.applicationService.applicationInsuredClient();
        if (!insuredClient) return;

        // Use the registered form group rather than reading via the application form tree:
        // the mailing substep is conditionally attached/detached by the stepper visibility
        // effect, so a tree lookup can race with a fresh load and bail before patching.
        const mailingAddressInformationForm = this.formRegistry.getFormGroup(
            MainSteps.PERSONAL_DETAILS,
            PersonalDetailsSubSteps.MAILING_ADDRESS,
        );

        if (!mailingAddressInformationForm) return;

        const mailingAddressInformationFormAddressGroup = mailingAddressInformationForm.get(
            MailingAddressInformationFormEnum.ADDRESS_GROUP,
        );

        mailingAddressInformationFormAddressGroup?.get(AddressFormEnum.ADDRESS)?.patchValue(insuredClient.mailing_address.mailing_address);
        mailingAddressInformationFormAddressGroup?.get(AddressFormEnum.CITY)?.patchValue(insuredClient.mailing_address.mailing_city);
        mailingAddressInformationFormAddressGroup
            ?.get(AddressFormEnum.STATE_CODE)
            ?.patchValue(insuredClient.mailing_address.mailing_state_code);
        mailingAddressInformationFormAddressGroup
            ?.get(AddressFormEnum.ZIP_CODE)
            ?.patchValue(insuredClient.mailing_address.mailing_zip_code);
        mailingAddressInformationFormAddressGroup
            ?.get(AddressFormEnum.ADDRESS2)
            ?.patchValue(insuredClient.mailing_address.mailing_address2);
        mailingAddressInformationFormAddressGroup?.get(AddressFormEnum.COUNTRY_CODE)?.patchValue(COUNTRY_CODE_UNITED_STATES);
        this.buildFullAddressFromFields(mailingAddressInformationFormAddressGroup);

        // Set validation state from existing DPV data
        this.addressValidationService.setValidationStateFromExisting(
            AddressType.INSURED_CLIENT_MAILING,
            insuredClient.mailing_address.mailing_dpv_code,
            insuredClient.mailing_address.mailing_dpv_data,
        );
    }

    private createModifiedInsuredClientData(): Partial<ClientResponse> {
        const currentValues = this.applicationService.rawApplication()?.insured_client;

        const dobValue = this.formRegistry.getValidatedControlValue<Date>(
            [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.DATE_OF_BIRTH],
            currentValues?.dob ? new Date(currentValues.dob) : null,
        );

        // IAPP2-1741: Fields removed
        // const contactTimeAmValue = this.formRegistry.getValidatedControlValue<number>(
        //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION, ContactInformationFormEnum.CONTACT_TIME_AM],
        //     currentValues?.contact_time_am,
        // );

        // const contactTimePmValue = this.formRegistry.getValidatedControlValue<number>(
        //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION, ContactInformationFormEnum.CONTACT_TIME_PM],
        //     currentValues?.contact_time_pm,
        // );

        const insuredClientFormData: Partial<ClientResponse> = {
            // Personal Information
            first_name: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.FIRST_NAME],
                currentValues?.first_name,
            ),
            middle_name:
                this.formRegistry.getValidatedControlValue<string>(
                    [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.MIDDLE_NAME],
                    currentValues?.middle_name,
                ) || null,
            last_name: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.LAST_NAME],
                currentValues?.last_name,
            ),
            dob: dobValue ? DateUtil.toServer(dobValue) : (currentValues?.dob ?? null),
            gender: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.SEX_AT_BIRTH],
                currentValues?.gender,
            ),
            // IAPP2-1741: Fields removed
            // smoker: this.formRegistry.getValidatedControlValue<number>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.SMOKER],
            //     currentValues?.smoker,
            // ),
            // smoker_ext: this.formRegistry.getValidatedControlValue<number>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.SMOKER_EXT],
            //     currentValues?.smoker_ext,
            // ),
            // weight: this.formRegistry.getValidatedControlValue<number>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.WEIGHT],
            //     currentValues?.weight,
            // ),
            // height_ft: this.formRegistry.getValidatedControlValue<number>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.HEIGHT_FT],
            //     currentValues?.height_ft,
            // ),
            // height_in: this.formRegistry.getValidatedControlValue<number>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION, PersonalInformationFormEnum.HEIGHT_IN],
            //     currentValues?.height_in,
            // ),

            // IAPP2-1741: Finance Information fields removed
            // occupation: this.formRegistry.getValidatedControlValue<string>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.FINANCE_INFORMATION, FinanceInformationFormEnum.OCCUPATION],
            //     currentValues?.occupation,
            // ),
            // annual_income_earned: this.formRegistry.getValidatedControlValue<number>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.FINANCE_INFORMATION, FinanceInformationFormEnum.ANNUAL_INCOME_EARNED],
            //     currentValues?.annual_income_earned,
            // ),
            // employer_name: this.formRegistry.getValidatedControlValue<string>(
            //     [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.FINANCE_INFORMATION, FinanceInformationFormEnum.EMPLOYER_NAME],
            //     currentValues?.employer_name,
            // ),
            // Residential Information
            address: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                    `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ADDRESS}`,
                ],
                currentValues?.address,
            ),
            city: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                    `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.CITY}`,
                ],
                currentValues?.city,
            ),
            state_code: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                    `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.STATE_CODE}`,
                ],
                currentValues?.state_code,
            ),
            zip_code: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                    `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ZIP_CODE}`,
                ],
                currentValues?.zip_code,
            ),
            address2: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                    `${ResidentialInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ADDRESS2}`,
                ],
                currentValues?.address2,
            ),
            country_code: COUNTRY_CODE_UNITED_STATES,
            has_mailing_address: this.formRegistry.getValidatedControlValue<number>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION,
                    ResidentialInformationFormEnum.HAS_MAILING_ADDRESS,
                ],
                currentValues?.has_mailing_address,
            ),

            // Mailing Address Information
            mailing_address: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.MAILING_ADDRESS,
                    `${MailingAddressInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ADDRESS}`,
                ],
                currentValues?.mailing_address,
            ),
            mailing_city: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.MAILING_ADDRESS,
                    `${MailingAddressInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.CITY}`,
                ],
                currentValues?.mailing_city,
            ),
            mailing_state_code: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.MAILING_ADDRESS,
                    `${MailingAddressInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.STATE_CODE}`,
                ],
                currentValues?.mailing_state_code,
            ),
            mailing_zip_code: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.MAILING_ADDRESS,
                    `${MailingAddressInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ZIP_CODE}`,
                ],
                currentValues?.mailing_zip_code,
            ),
            mailing_address2: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.MAILING_ADDRESS,
                    `${MailingAddressInformationFormEnum.ADDRESS_GROUP}.${AddressFormEnum.ADDRESS2}`,
                ],
                currentValues?.mailing_address2,
            ),

            // Contact Information
            email: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION, ContactInformationFormEnum.EMAIL],
                currentValues?.email,
            ),
            primary_phone: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION, ContactInformationFormEnum.PRIMARY_PHONE],
                currentValues?.primary_phone,
            ),
            primary_phone_type: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION, ContactInformationFormEnum.PRIMARY_PHONE_TYPE],
                currentValues?.primary_phone_type,
            ),
            phone2:
                this.formRegistry.getValidatedControlValue<string>(
                    [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION, ContactInformationFormEnum.PHONE2],
                    currentValues?.phone2,
                ) || null,
            phone2_type: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION, ContactInformationFormEnum.PHONE2_TYPE],
                currentValues?.phone2_type,
            ),
            // IAPP2-1741: Fields removed
            // contact_time_am:
            //     contactTimeAmValue !== null ? NumberUtil.toNumber(contactTimeAmValue) : (currentValues?.contact_time_am ?? null),
            // contact_time_pm:
            //     contactTimePmValue !== null ? NumberUtil.toNumber(contactTimePmValue) : (currentValues?.contact_time_pm ?? null),

            // Residency and Identification
            ssn: this.formRegistry.getValidatedControlValue<string>(
                [MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT, IdentityVerificationConsentFormEnum.SSN],
                currentValues?.ssn,
            ),

            birth_country_code: this.formRegistry.getValidatedControlValue<string>(
                [
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION,
                    ResidencyAndIdentificationFormEnum.BIRTH_COUNTRY_CODE,
                ],
                currentValues?.birth_country_code,
            ),
            birth_state_code: [COUNTRY_CODE_UNITED_STATES, COUNTRY_CODE_CANADA].includes(
                this.formRegistry.getSafeControlValue<string>([
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION,
                    ResidencyAndIdentificationFormEnum.BIRTH_COUNTRY_CODE,
                ])!,
            )
                ? this.formRegistry.getValidatedControlValue<string>(
                      [
                          MainSteps.PERSONAL_DETAILS,
                          PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION,
                          ResidencyAndIdentificationFormEnum.BIRTH_STATE_CODE,
                      ],
                      currentValues?.birth_state_code,
                  )
                : null,
            us_citizen:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION,
                    ResidencyAndIdentificationFormEnum.US_CITIZEN,
                ]) ??
                currentValues?.us_citizen ??
                null,

            // Identity Verification Consent - use getSafeControlValue (not getValidatedControlValue)
            // because the form has requiredNumericTrue validator that makes it invalid when NO (0) is selected,
            // but we still need to save the 0 value to the backend
            giact_disclosure:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT,
                    IdentityVerificationConsentFormEnum.GIACT_DISCLOSURE,
                ]) ??
                currentValues?.giact_disclosure ??
                null,

            // Role
            role: ActorType.INSURED,
        };

        // Only include residential DPV if validation was performed
        const residentialDpv = this.addressValidationService.getValidationState(AddressType.INSURED_CLIENT_RESIDENTIAL)();
        if (residentialDpv.dpvData) {
            insuredClientFormData.address_dpv_code = residentialDpv.dpvCode?.toLowerCase() ?? null;
            insuredClientFormData.address_dpv_data = JSON.stringify(residentialDpv.dpvData) as unknown as AddressDpvDataResponse;
        }

        // Only include mailing DPV if validation was performed
        const mailingDpv = this.addressValidationService.getValidationState(AddressType.INSURED_CLIENT_MAILING)();
        if (mailingDpv.dpvData) {
            insuredClientFormData.mailing_dpv_code = mailingDpv.dpvCode?.toLowerCase() ?? null;
            insuredClientFormData.mailing_dpv_data = JSON.stringify(mailingDpv.dpvData) as unknown as AddressDpvDataResponse;
        }

        // Then do change detection on filtered data
        const changedValues = Object.entries(insuredClientFormData).reduce((accumulator, [key, value]) => {
            if (currentValues?.[key as keyof ClientResponse] !== value) {
                (accumulator as Record<string, unknown>)[key] = value;
            }
            return accumulator;
        }, {} as Partial<ClientResponse>);

        return changedValues;
    }

    private createInsuredClient(applicationId: number, insuredClientData: Partial<ClientResponse>) {
        const url = `${API_URL}/applications/${applicationId}/clients`;
        return this.http.post<ClientResponse>(url, insuredClientData);
    }

    private updateInsuredClient(applicationId: number, insuredClientId: number, insuredClientData: Partial<ClientResponse>) {
        const url = `${API_URL}/applications/${applicationId}/clients/${insuredClientId}`;
        return this.http.patch<ClientResponse>(url, insuredClientData);
    }

    createOwnerDifferentThanInsuredClient(applicationId: number) {
        const url = `${API_URL}/applications/${applicationId}/clients`;
        const payload: Partial<ClientResponse> = {
            role: ActorType.OWNER,
        };
        return this.http.delete<ClientResponse>(url, { body: payload });
    }

    createPrimaryPayorDifferentThanInsuredClient(applicationId: number) {
        const url = `${API_URL}/applications/${applicationId}/clients`;
        const payload: Partial<ClientResponse> = {
            role: ActorType.PRIMARY_PAYER,
        };
        return this.http.delete<ClientResponse>(url, { body: payload });
    }

    createSecondaryPayorDifferentThanInsuredClient(applicationId: number) {
        const url = `${API_URL}/applications/${applicationId}/clients`;
        const payload: Partial<ClientResponse> = {
            role: ActorType.SECONDARY_PAYER,
        };
        return this.http.delete<ClientResponse>(url, { body: payload });
    }

    updateUsCitizen(value: number) {
        const applicationId = this.applicationService.application()?.id;
        const insuredClientId = this.applicationService.application()?.insured_client?.id;

        if (!applicationId || !insuredClientId) {
            return undefined;
        }

        return this.updateInsuredClient(applicationId, insuredClientId, { us_citizen: value }).pipe(
            tap((response: ClientResponse) => {
                if (response?.id) {
                    this.suppressNextPatch.set(true);
                    this.applicationService.insuredClientFromResponse.set(response);
                } else {
                    this.applicationService.refreshApplicationData.update((n) => n + 1);
                }
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    updateGiactDisclosure(value: number) {
        const applicationId = this.applicationService.application()?.id;
        const insuredClientId = this.applicationService.application()?.insured_client?.id;

        if (!applicationId || !insuredClientId) {
            return undefined;
        }

        return this.updateInsuredClient(applicationId, insuredClientId, { giact_disclosure: value }).pipe(
            tap((response: ClientResponse) => {
                if (response?.id) {
                    this.suppressNextPatch.set(true);
                    this.applicationService.insuredClientFromResponse.set(response);
                } else {
                    this.applicationService.refreshApplicationData.update((n) => n + 1);
                }
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    updateInsuredClientWithGiactResult(giactResponse: { status: number; outcome: string }) {
        const applicationId = this.applicationService.application()?.id;
        const insuredClientId = this.applicationService.application()?.insured_client?.id;

        if (!applicationId || !insuredClientId) {
            return;
        }

        const giactData: Partial<ClientResponse> = {
            giact_status: giactResponse.status,
            giact_outcome: giactResponse.outcome,
        };

        return this.updateInsuredClient(applicationId, insuredClientId, giactData).pipe(
            tap((response: ClientResponse) => {
                if (response && response.id) {
                    this.applicationService.insuredClientFromResponse.set(response);
                } else {
                    this.applicationService.refreshApplicationData.update((n) => n + 1);
                }
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    private buildFullAddressFromFields(addressGroup: AbstractControl | null | undefined): void {
        if (!addressGroup) return;

        const parts = [
            addressGroup.get(AddressFormEnum.ADDRESS)?.value,
            addressGroup.get(AddressFormEnum.ADDRESS2)?.value,
            addressGroup.get(AddressFormEnum.CITY)?.value,
            addressGroup.get(AddressFormEnum.STATE_CODE)?.value,
            addressGroup.get(AddressFormEnum.ZIP_CODE)?.value,
        ].filter(Boolean);

        addressGroup.get(AddressFormEnum.FULL_ADDRESS)?.patchValue(parts.join(', '), { emitEvent: false });
    }
}
